import React from 'react';

interface PestelFactor {
  factor: string;
  positive: string[];
  negative: string[];
}

interface PestelImpactChartProps {
  data: PestelFactor[];
  height?: number;
}

const PestelImpactChart: React.FC<PestelImpactChartProps> = ({ data, height = 400 }) => {
  // Calculate max values for scaling
  const maxPositive = Math.max(...data.map((d) => d.positive.length), 1);
  const maxNegative = Math.max(...data.map((d) => d.negative.length), 1);
  const maxTotal = Math.max(maxPositive, maxNegative);

  // Chart dimensions
  const width = 600;
  const margin = { top: 20, right: 80, bottom: 40, left: 80 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const barHeight = (chartHeight / data.length) * 0.7;
  const barSpacing = (chartHeight / data.length) * 0.3;

  // Colors
  const positiveColor = '#16a34a'; // green-600
  const negativeColor = '#dc2626'; // red-600

  // Factor colors for labels
  const factorColors = {
    Political: '#8b5cf6', // violet-500
    Economic: '#059669', // emerald-600
    Social: '#2563eb', // blue-600
    Technological: '#7c3aed', // violet-600
    Environmental: '#16a34a', // green-600
    Legal: '#dc2626', // red-600
  };

  const getFactorColor = (factor: string): string => {
    return factorColors[factor as keyof typeof factorColors] || '#6b7280';
  };

  // Calculate overall sentiment for each factor
  const getFactorSentiment = (positive: number, negative: number): string => {
    if (positive === 0 && negative === 0) return 'No Analysis';
    const ratio = positive / (positive + negative);
    if (ratio >= 0.7) return 'Highly Positive';
    if (ratio >= 0.6) return 'Positive';
    if (ratio >= 0.4) return 'Balanced';
    if (ratio >= 0.3) return 'Negative';
    return 'Highly Negative';
  };

  const getSentimentColor = (sentiment: string): string => {
    switch (sentiment) {
      case 'Highly Positive':
        return '#16a34a';
      case 'Positive':
        return '#65a30d';
      case 'Balanced':
        return '#d97706';
      case 'Negative':
        return '#ea580c';
      case 'Highly Negative':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  return (
    <div className="flex flex-col items-center" data-chart-type="pestel">
      <svg width={width} height={height} className="overflow-visible">
        <defs>
          <linearGradient id="positiveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={positiveColor} stopOpacity="0.7" />
            <stop offset="100%" stopColor={positiveColor} stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="negativeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={negativeColor} stopOpacity="0.9" />
            <stop offset="100%" stopColor={negativeColor} stopOpacity="0.7" />
          </linearGradient>
        </defs>

        {/* Background */}
        <rect
          x={margin.left}
          y={margin.top}
          width={chartWidth}
          height={chartHeight}
          fill="#f8fafc"
          stroke="#e2e8f0"
          strokeWidth="1"
          rx="4"
        />

        {/* Center line */}
        <line
          x1={margin.left + chartWidth / 2}
          y1={margin.top}
          x2={margin.left + chartWidth / 2}
          y2={margin.top + chartHeight}
          stroke="#6b7280"
          strokeWidth="2"
          strokeDasharray="4,4"
          opacity="0.5"
        />

        {/* Grid lines for scale */}
        {[...Array(maxTotal + 1)].map((_, i) => {
          const x1 = margin.left + chartWidth / 2 + (i / maxTotal) * (chartWidth / 2);
          const x2 = margin.left + chartWidth / 2 - (i / maxTotal) * (chartWidth / 2);

          return (
            <g key={i}>
              {/* Positive side grid */}
              <line
                x1={x1}
                y1={margin.top}
                x2={x1}
                y2={margin.top + chartHeight}
                stroke="#e2e8f0"
                strokeWidth="1"
                opacity="0.5"
              />
              {/* Negative side grid */}
              <line
                x1={x2}
                y1={margin.top}
                x2={x2}
                y2={margin.top + chartHeight}
                stroke="#e2e8f0"
                strokeWidth="1"
                opacity="0.5"
              />
            </g>
          );
        })}

        {/* Data bars */}
        {data.map((factor, index) => {
          const y = margin.top + index * (barHeight + barSpacing) + barSpacing / 2;
          const centerX = margin.left + chartWidth / 2;

          // Positive bar (right side)
          const positiveWidth = (factor.positive.length / maxTotal) * (chartWidth / 2);
          // Negative bar (left side)
          const negativeWidth = (factor.negative.length / maxTotal) * (chartWidth / 2);

          return (
            <g key={factor.factor}>
              {/* Negative bar (left side) */}
              <rect
                x={centerX - negativeWidth}
                y={y}
                width={negativeWidth}
                height={barHeight}
                fill="url(#negativeGradient)"
                stroke="#dc2626"
                strokeWidth="1"
                opacity="0.8"
                rx="2"
              />

              {/* Positive bar (right side) */}
              <rect
                x={centerX}
                y={y}
                width={positiveWidth}
                height={barHeight}
                fill="url(#positiveGradient)"
                stroke="#16a34a"
                strokeWidth="1"
                opacity="0.8"
                rx="2"
              />

              {/* Factor label (left side) */}
              <text
                x={margin.left - 10}
                y={y + barHeight / 2}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-gray-700 text-sm font-semibold"
                fill={getFactorColor(factor.factor)}
              >
                {factor.factor}
              </text>

              {/* Count labels */}
              {factor.negative.length > 0 && (
                <text
                  x={centerX - negativeWidth / 2}
                  y={y + barHeight / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-white text-xs font-bold"
                >
                  {factor.negative.length}
                </text>
              )}

              {factor.positive.length > 0 && (
                <text
                  x={centerX + positiveWidth / 2}
                  y={y + barHeight / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-white text-xs font-bold"
                >
                  {factor.positive.length}
                </text>
              )}

              {/* Sentiment indicator (right side) */}
              <g>
                <circle
                  cx={margin.left + chartWidth + 20}
                  cy={y + barHeight / 2}
                  r="6"
                  fill={getSentimentColor(
                    getFactorSentiment(factor.positive.length, factor.negative.length)
                  )}
                  stroke="white"
                  strokeWidth="2"
                />
                <text
                  x={margin.left + chartWidth + 35}
                  y={y + barHeight / 2}
                  dominantBaseline="middle"
                  className="fill-gray-600 text-xs"
                >
                  {getFactorSentiment(factor.positive.length, factor.negative.length)}
                </text>
              </g>
            </g>
          );
        })}

        {/* Axis labels */}
        <text
          x={margin.left + chartWidth / 4}
          y={height - 10}
          textAnchor="middle"
          className="fill-red-600 text-sm font-medium"
        >
          ← Threats
        </text>

        <text
          x={margin.left + (chartWidth * 3) / 4}
          y={height - 10}
          textAnchor="middle"
          className="fill-green-600 text-sm font-medium"
        >
          Opportunities →
        </text>

        {/* Scale indicators */}
        {[...Array(maxTotal + 1)].map((_, i) => (
          <g key={i}>
            <text
              x={margin.left + chartWidth / 2 + (i / maxTotal) * (chartWidth / 2)}
              y={height - 25}
              textAnchor="middle"
              className="fill-gray-500 text-xs"
            >
              {i}
            </text>
            <text
              x={margin.left + chartWidth / 2 - (i / maxTotal) * (chartWidth / 2)}
              y={height - 25}
              textAnchor="middle"
              className="fill-gray-500 text-xs"
            >
              {i}
            </text>
          </g>
        ))}
      </svg>

      {/* Legend */}
      <div className="mt-6 grid grid-cols-2 gap-8 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-red-600 rounded"></div>
          <span className="text-red-700 font-medium">Negative Impacts (Threats)</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-green-600 rounded"></div>
          <span className="text-green-700 font-medium">Positive Impacts (Opportunities)</span>
        </div>
      </div>

      {/* Sentiment Legend */}
      <div className="mt-4 grid grid-cols-5 gap-2 text-xs">
        {['Highly Negative', 'Negative', 'Balanced', 'Positive', 'Highly Positive'].map(
          (sentiment) => (
            <div key={sentiment} className="text-center">
              <div
                className="w-3 h-3 rounded-full mx-auto mb-1"
                style={{ backgroundColor: getSentimentColor(sentiment) }}
              ></div>
              <span style={{ color: getSentimentColor(sentiment) }} className="font-medium">
                {sentiment.replace(' ', '\n')}
              </span>
            </div>
          )
        )}
      </div>

      <div className="text-xs text-gray-500 text-center mt-2">
        Balance of opportunities vs threats across external factors
      </div>
    </div>
  );
};

export default PestelImpactChart;
